import { useEffect, useMemo, useRef, useState } from "react";

import {
  formatOfficeVesselOptionLabel,
  OFFICE_VESSEL_SCOPE_ALL_LABEL,
} from "../../lib/officeVesselScope";
import type { AuthVesselOption } from "../../types/auth";
import type { VesselId } from "../../types/vessel";

interface OfficeVesselScopeSelectProps {
  options: AuthVesselOption[];
  value: VesselId | null;
  onChange: (nextValue: VesselId | null) => void;
}

export function OfficeVesselScopeSelect({
  options,
  value,
  onChange,
}: OfficeVesselScopeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<globalThis.HTMLDivElement | null>(null);
  const listId = "office-vessel-scope-options";
  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) ?? null,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) =>
      [option.vessel_code, option.vessel_name].join(" ").toLowerCase().includes(normalizedSearch),
    );
  }, [options, search]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      return;
    }

    const handleMouseDown = (event: globalThis.MouseEvent) => {
      const target = event.target as globalThis.Node | null;
      if (target && rootRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const selectedLabel = selectedOption
    ? formatOfficeVesselOptionLabel(selectedOption)
    : OFFICE_VESSEL_SCOPE_ALL_LABEL;

  return (
    <div className="relative min-w-[220px] max-w-[280px]" ref={rootRef}>
      <button
        aria-controls={listId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Vessel Scope"
        className="flex min-h-11 w-full items-center justify-between rounded-md bg-white px-2.5 py-2 text-left transition hover:bg-gray-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
        role="combobox"
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen((current) => !current);
          }
        }}
      >
        <span className="min-w-0">
          <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
            Vessel Scope
          </span>
          <span className="mt-0.5 block truncate text-sm font-semibold text-gray-900">
            {selectedLabel}
          </span>
        </span>
        <span className="ml-3 flex shrink-0 items-center">
          <svg
            aria-hidden="true"
            className={`h-4 w-4 text-gray-400 transition ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
            />
          </svg>
        </span>
      </button>

      {isOpen ? (
        <div
          className="absolute right-0 z-20 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-md border border-gray-200 bg-white shadow-md"
          id={listId}
          role="listbox"
        >
          <div className="space-y-2 border-b border-gray-100 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Filter office screens
              </p>
              <span className="text-xs text-gray-500">{options.length} vessels</span>
            </div>
            <input
              aria-label="Search vessels"
              className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
              placeholder="Search by vessel code or name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            <button
              aria-label={OFFICE_VESSEL_SCOPE_ALL_LABEL}
              aria-selected={value === null}
              className={`block w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 ${
                value === null ? "bg-blue-50" : ""
              }`}
              role="option"
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
            >
              <span className="block text-sm font-semibold text-gray-900">
                {OFFICE_VESSEL_SCOPE_ALL_LABEL}
              </span>
              <span className="block text-xs text-gray-500">
                Show every vessel in your office scope.
              </span>
            </button>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.id === value;
                return (
                  <button
                    key={option.id}
                    aria-label={formatOfficeVesselOptionLabel(option)}
                    aria-selected={isSelected}
                    className={`block w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-blue-50 ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                    role="option"
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onChange(option.id);
                      setIsOpen(false);
                    }}
                  >
                    <span className="block text-sm font-semibold text-gray-900">
                      {formatOfficeVesselOptionLabel(option)}
                    </span>
                    <span className="block text-xs text-gray-500">
                      Filter to this vessel only.
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-3 text-sm text-gray-500">No vessels match your search.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
